import { FileUpload } from '../../Api/FileUpload';
import {
  createKnowledgeLibraryItem,
  getKnowledgeLibrarySyncStatus,
  knowledgeLibraryErrorMessage,
  syncKnowledgeLibrary,
} from '../../Api/KnowledgeLibraryApi';
import { libraryFileProblem } from '../../Components/KnowledgeLibrary/knowledgeLibraryConfig';
import { dropBody } from './knowledgeHelpers';

/**
 * The drop-in pipeline: for each file, upload it to the file service and create a Published
 * upload row; then run one library sync for the batch and follow each row in the AI status
 * until it is indexed or failed. One file failing never stops the others.
 *
 * Progress states per file: queued, uploading, uploaded, indexing, indexed, error (with message).
 */

export const POLL_MS = 5000;
export const POLL_LIMIT = 24;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const readStatus = async () => {
  try {
    return (await getKnowledgeLibrarySyncStatus())?.data || null;
  } catch {
    return null;
  }
};

/**
 * One sync for the batch. On a 409 another sync is running: wait until the status shows that run
 * ended (its last_run_at changes), then try once more, since the running sync may have read the
 * feed before these rows existed.
 */
export async function startBatchSync({ pollMs = POLL_MS, pollLimit = POLL_LIMIT, alive = () => true } = {}) {
  try {
    return await syncKnowledgeLibrary();
  } catch (error) {
    if (error?.response?.status !== 409) throw error;
  }
  const before = (await readStatus())?.cursor?.last_run_at || null;
  for (let n = 0; n < pollLimit && alive(); n += 1) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(pollMs);
    // eslint-disable-next-line no-await-in-loop
    const status = await readStatus();
    const lastRun = status?.cursor?.last_run_at;
    if (lastRun && lastRun !== before) break;
  }
  return syncKnowledgeLibrary();
}

/** A row's progress from its AI status row: indexed, an error with the reason, or still indexing. */
export function progressFromStatus(statusItem) {
  if (!statusItem) return { state: 'indexing' };
  if (statusItem.state === 'synced') return { state: 'indexed' };
  if (statusItem.state === 'failed') return { state: 'error', message: statusItem.error || 'Indexing failed' };
  if (statusItem.state === 'no_content') return { state: 'error', message: 'No text could be indexed from this file' };
  return { state: 'indexing' };
}

/**
 * Runs the pipeline. files: File[]; details: the Details section; extraFor(file): version or
 * amendment fields; onUpdate(index, patch) reports progress. alive() stops it after an unmount.
 */
export async function indexFiles({
  files,
  details,
  extraFor = () => ({}),
  onUpdate,
  pollMs = POLL_MS,
  pollLimit = POLL_LIMIT,
  alive = () => true,
}) {
  const single = files.length === 1;
  const created = [];
  for (let i = 0; i < files.length; i += 1) {
    if (!alive()) return;
    const file = files[i];
    const problem = libraryFileProblem(file);
    if (problem) {
      onUpdate(i, { state: 'error', message: problem });
      continue; // eslint-disable-line no-continue
    }
    try {
      onUpdate(i, { state: 'uploading', message: '' });
      // eslint-disable-next-line no-await-in-loop
      const upload = await FileUpload({
        file,
        file_name: file.name,
        file_type: file.type,
        mime_type: file.type,
        file_size: file.size,
        created_by: localStorage.getItem('accountId'),
        related_model_type: 'knowledge_library',
      });
      const fileId = upload?.data?.data?.id;
      if (!fileId) throw new Error('The file service returned no file id');
      // eslint-disable-next-line no-await-in-loop
      const row = await createKnowledgeLibraryItem(dropBody({ fileName: file.name, fileId, details, single, extra: extraFor(file) }));
      const id = row?.data?.id;
      onUpdate(i, { state: 'uploaded', itemId: id, title: row?.data?.title });
      if (id) created.push({ i, id: String(id) });
    } catch (error) {
      onUpdate(i, { state: 'error', message: knowledgeLibraryErrorMessage(error, error?.message || `Could not add ${file.name}`) });
    }
  }
  if (!created.length || !alive()) return;

  created.forEach(({ i }) => onUpdate(i, { state: 'indexing' }));
  try {
    await startBatchSync({ pollMs, pollLimit, alive });
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const message = error?.response?.status === 409
      ? 'A sync is still running. The file is saved; run Sync now later to index it.'
      : `Indexing did not start: ${typeof detail === 'string' ? detail : knowledgeLibraryErrorMessage(error, 'the sync failed')}`;
    created.forEach(({ i }) => onUpdate(i, { state: 'error', message }));
    return;
  }

  let waiting = created;
  for (let n = 0; n < pollLimit && waiting.length && alive(); n += 1) {
    // eslint-disable-next-line no-await-in-loop
    const status = await readStatus();
    const byId = new Map((status?.items || []).map((s) => [String(s.library_item_id), s]));
    waiting = waiting.filter(({ i, id }) => {
      const progress = progressFromStatus(byId.get(id));
      if (progress.state === 'indexing') return true;
      onUpdate(i, progress);
      return false;
    });
    // eslint-disable-next-line no-await-in-loop
    if (waiting.length) await sleep(pollMs);
  }
  if (alive()) waiting.forEach(({ i }) => onUpdate(i, { state: 'indexing', message: 'Still indexing. Check back later.' }));
}
